/**
 * ADA patch: remove "blank" keyboard focus stops from third-party launcher
 * widgets (off-screen / collapsed / unnamed focusable elements that stay in
 * the tab order). No core file changes.
 *
 * NOTE: This does NOT touch the HubSpot chat launcher. That launcher button
 * lives inside a cross-origin iframe (#hubspot-conversations-iframe) which is
 * named and visible, so it is intentionally left alone — its focus behaviour
 * is controlled by HubSpot and cannot be altered by theme code.
 */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button, input, select, textarea, iframe, [tabindex]';

  /**
   * First-party theme UI this patch must never touch.
   *
   * These are real, keyboard-operable interfaces (cart drawer, nav drawer,
   * filter drawer, modals). They are position:fixed and slide in over a
   * ~450ms transform transition, so a scan triggered by the "open" class
   * change runs while they are still off-screen — which the visibility
   * heuristics below would misread as "hidden" and remove from the tab order.
   */
  var THEME_UI = [
    '.drawer',
    '.modal',
    '[role="dialog"]',
    '[aria-modal="true"]'
  ].join(',');

  function isThemeUI(el) {
    return !!(el.closest && el.closest(THEME_UI));
  }

  var shiftHeld = false;
  document.addEventListener(
    'keydown',
    function (e) {
      if (e.key === 'Tab') shiftHeld = e.shiftKey;
    },
    true
  );

  function isFixed(el) {
    var node = el;
    while (node && node !== document.body) {
      if (window.getComputedStyle(node).position === 'fixed') return true;
      node = node.parentElement;
    }
    return false;
  }

  /**
   * Positive identification of a third-party widget, by name. Unchanged from
   * the original heuristics — these signals still take priority over the
   * first-party exclusion below, so HubSpot/Calendly/chat launcher cleanup
   * keeps working exactly as before.
   */
  function isKnownWidget(el) {
    if (el.tagName === 'IFRAME') return true;
    var idc = (el.id + ' ' + (typeof el.className === 'string' ? el.className : '')).toLowerCase();
    if (/hubspot|podium|chat|widget|launcher|intercom|drift|tidio|gorgias/.test(idc)) return true;
    if (
      el.closest &&
      el.closest('[id*="hubspot" i],[id*="podium" i],[class*="chat" i],[class*="widget" i],[class*="launcher" i]')
    ) {
      return true;
    }
    return false;
  }

  function isWidgetScoped(el) {
    if (isKnownWidget(el)) return true;
    // Not a named widget: fall back to "unowned fixed-position overlay", but
    // never claim first-party theme UI (drawers/modals) that way.
    if (isThemeUI(el)) return false;
    return isFixed(el);
  }

  function accessibleName(el) {
    var name =
      (el.getAttribute && (el.getAttribute('aria-label') || el.getAttribute('title'))) ||
      (el.textContent || '');
    name = (name || '').trim();
    if (name) return name;
    var img = el.querySelector && el.querySelector('img[alt]');
    if (img && (img.getAttribute('alt') || '').trim()) return img.getAttribute('alt').trim();
    return '';
  }

  function visibleArea(el) {
    var r = el.getBoundingClientRect();
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var w = Math.max(0, Math.min(r.right, vw) - Math.max(r.left, 0));
    var h = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));
    return w * h;
  }

  function isEffectivelyInvisible(el) {
    var style = window.getComputedStyle(el);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      parseFloat(style.opacity) === 0 ||
      el.getAttribute('aria-hidden') === 'true'
    ) {
      return true;
    }
    var r = el.getBoundingClientRect();
    if (r.width < 4 || r.height < 4) return true;
    if (visibleArea(el) < 400) return true; // less than ~20x20px on screen
    return false;
  }

  function shouldSuppress(el) {
    // Never suppress the real, named, visible chat iframe.
    if (el.id === 'hubspot-conversations-iframe') return false;
    // Never touch the Calendly popup — its own patch manages its focus trap.
    if (el.closest && el.closest('.calendly-overlay')) return false;
    if (!isWidgetScoped(el)) return false;
    return isEffectivelyInvisible(el) || !accessibleName(el);
  }

  function suppress(el) {
    if (el.getAttribute('tabindex') === '-1') return;
    // Remember the author's own tabindex so restoring cannot silently drop it.
    el.dataset.adaFocusPrev = el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : '';
    el.setAttribute('tabindex', '-1');
    el.dataset.adaFocusHandled = 'removed';
  }

  function restore(el) {
    if (el.dataset.adaFocusHandled !== 'removed') return;
    if (el.dataset.adaFocusPrev) {
      el.setAttribute('tabindex', el.dataset.adaFocusPrev);
    } else {
      el.removeAttribute('tabindex');
    }
    delete el.dataset.adaFocusPrev;
    delete el.dataset.adaFocusHandled;
  }

  function scan() {
    var els = document.querySelectorAll(FOCUSABLE);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (shouldSuppress(el)) {
        suppress(el);
      } else {
        restore(el);
      }
    }
  }

  function tabbableList() {
    var out = [];
    var els = document.querySelectorAll(FOCUSABLE);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.getAttribute('tabindex') === '-1') continue;
      if (el.disabled) continue;
      out.push(el);
    }
    return out;
  }

  function moveFocusPast(badEl) {
    var list = tabbableList();
    var idx = list.indexOf(badEl);
    if (idx === -1) return false;
    var step = shiftHeld ? -1 : 1;
    for (var i = idx + step; i >= 0 && i < list.length; i += step) {
      var c = list[i];
      if (!isWidgetScoped(c) || (!isEffectivelyInvisible(c) && accessibleName(c))) {
        c.focus();
        return document.activeElement === c;
      }
    }
    return false;
  }

  document.addEventListener('focusin', function (evt) {
    var el = evt.target;
    if (!el || el === document.body || !el.getBoundingClientRect) return;
    if (!shouldSuppress(el)) return;
    suppress(el);
    if (!moveFocusPast(el) && typeof el.blur === 'function') el.blur();
  });

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      scan();
    });
  }

  function init() {
    scan();
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'tabindex', 'aria-hidden', 'src']
    });
    [500, 1500, 3000, 6000].forEach(function (t) {
      window.setTimeout(scan, t);
    });
    window.addEventListener('resize', schedule);
    // Widget panels that slide/fade in are still off-screen one frame after
    // their "open" class lands, so the mutation-driven scan sees them as
    // hidden. Re-scan once the animation actually finishes.
    //
    // Kept deliberately narrow: only movement/fade properties, and never for
    // theme UI (already exempt above). Without this filter every hover
    // transition on a collection page would trigger a full relayout scan.
    function onMotionEnd(e) {
      var prop = e.propertyName;
      if (prop && prop !== 'transform' && prop !== 'opacity' && prop !== 'visibility') return;
      if (e.target && e.target.nodeType === 1 && isThemeUI(e.target)) return;
      schedule();
    }
    document.addEventListener('transitionend', onMotionEnd, true);
    document.addEventListener('animationend', onMotionEnd, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
